/**
 * Pairing, Push and Pull: the whole of what this device does with a Jot Server.
 *
 * **No body from a Jot Server is written by this module.** That was once "no body
 * from a Jot Server is ever written locally", and pull (issue #6) is what narrowed
 * it — but the structural half is unchanged and is what keeps the narrowing honest:
 * this module still holds a read-only view of note storage,
 * `Pick<NotesStore, 'get'>`, so there is no call in here that could persist a Note
 * at all. Widening that dep is how this change could have reintroduced the worst
 * bug this project has had.
 *
 * Both writes of the user's text are therefore the panel's, and for the same
 * reason. Pairing re-keys the local Scratchpad to the id the server named
 * (`adoptScratchpad`); a pull puts the server's body at it (`applyPulledBody`).
 * Neither is delegation for its own sake: the bytes that belong at the adopted id
 * are the ones the editor holds at the instant the id changes, and whether a pulled
 * body may be written at all depends on the editor's live text — which a module
 * with no React in it cannot see. A body read here before a network round trip is
 * by then a guess, and the guard is a compare-and-swap against that guess
 * (`./pull-guard`).
 *
 * Deliberately free of React, `window` and `document`, and it stays that way:
 * every decision below is reachable from a plain Node process driving it against
 * a real server, which is the only way the interesting cases (a 409 between two
 * devices, a wrong token, a refused endpoint) can be exercised at all.
 *
 * The order inside `runOnce` is the design. Read settings, then Sync State, then
 * the local body — and only then construct a client. Every reason not to talk to
 * a server is established before anything exists that could: sync switched off,
 * no token, an endpoint the token may not travel over, a Pending Conflict
 * already waiting, a local Scratchpad this device could not read.
 */

import { isNoteId } from '@shared/sync-state'
import type { ScratchpadRecord } from '@shared/sync-wire'
import type {
  Note,
  NoteId,
  NotesStore,
  PlatformInfo,
  SettingsStore,
  SyncSettings,
  SyncState,
  SyncStateStore,
} from '@shared/types'
import {
  createScratchpadClient,
  type PushOutcome,
  type ScratchpadClient,
  type SyncFailure,
} from './client'
import { scratchpadEndpoint, type EndpointProblem } from './endpoint'
import type { ComparedScratchpad, PullTarget, PullVerdict } from './pull-guard'

/** Why the engine is not going to touch the network this time. */
export type SyncUnavailable =
  /** Sync State could not be read or written. Never guessed at: see below. */
  | 'sync-state'
  /**
   * The local Scratchpad is not something this device can reach right now: the
   * body could not be read — which is not the same fact as "it is empty" — or the
   * panel had nothing open to re-key, or the Note the panel wrote at the adopted
   * id could not be read back, or storage holds no Note while the editor holds the
   * only copy of its text (`unpersisted`). All of them mean the same thing to the user
   * and to the next pass: nothing was sent, nothing was recorded, look again shortly.
   */
  | 'scratchpad'

/**
 * What sync is waiting for. Nothing was written and nothing is wrong: this clears
 * itself, which is what separates it from a Conflict.
 *
 * Still one member, and the reason it was named rather than inlined has now been
 * spent the other way round: issue #6 was expected to widen this, and instead it
 * replaced what the single member *means* while leaving the status shape and the
 * glossary term exactly where they were.
 */
export type SyncWaitingFor =
  /**
   * The Jot Server holds a body this device has not copied down yet.
   *
   * It used to mean "and it never can, because this slice cannot pull". It now means
   * the narrower thing the guard reports: a write is still queued, so a pulled body
   * may not be written across it (`./pull-guard`). Nothing is recorded either way,
   * and it clears itself the moment the save lands — which is what Waiting has always
   * been (CONTEXT.md), and why the term did not have to move.
   */
  'pull'

export type SyncStatus =
  /** Off, or no token: nothing was sent, and nothing was recorded. */
  | { kind: 'off' }
  /** The endpoint is not one the token may travel over. Nothing was sent. */
  | { kind: 'refused'; problem: EndpointProblem }
  /**
   * Nothing to push: the local body is the one this device last agreed with the
   * Jot Server, at revision `rev` — or `0` when neither side holds anything yet.
   * `pushed` says whether this pass is what established that.
   *
   * Still a fact about this device and its agreement rather than the claim that the
   * two hold the same body *now*, and the wording survived pull deliberately. A pass
   * that pulls does ask, so most of these are now the stronger fact; but two are not,
   * and reporting them as this rather than inventing an outcome is what keeps the
   * meaning stable. A `409` on the push side is still where a server that moved is
   * discovered, measured against the Last-Agreed Body rather than assumed. And a
   * `GET` that answers `404` while this device holds an agreement — a Jot Server
   * whose database was replaced — lands here too: the agreement is a fact about the
   * past that a wiped relay cannot unmake, nothing is recorded and nothing is
   * written, which is exactly what every pass reported before pull existed. Putting
   * that server's Scratchpad back is not this issue's business (see `runOnce`).
   */
  | { kind: 'agreed'; rev: number; pushed: boolean }
  /** A Pending Conflict is recorded and sync has stopped pushing (ADR-0002). */
  | { kind: 'conflict'; record: ScratchpadRecord }
  /**
   * Not converged, not a Conflict, and nothing recorded: the record Pairing was
   * working from, and what it is waiting for. Every later pass re-reads and
   * reports again, so no state has to be cleared to escape this.
   */
  | { kind: 'waiting'; what: SyncWaitingFor; record: ScratchpadRecord }
  | { kind: 'unavailable'; what: SyncUnavailable }
  | SyncFailure

/**
 * What came of asking the panel to take on the Scratchpad id Pairing is adopting.
 *
 * There is no "refused" among these, and that absence is the fix for a defect this
 * module carried twice. A refusal that arrives *after* a Note has been written
 * leaves storage holding a record the engine invented, and the previous shape made
 * that the ordinary outcome of typing during the pairing round trip. Adopting is
 * now a decision the panel takes and commits in one synchronous step, before
 * anything is written, so the only answers are "done", "there was nothing open to
 * re-key" and "the write did not land".
 */
export type AdoptOutcome =
  /** The panel follows the id, and this is the Note now stored there. */
  | { kind: 'adopted'; note: Note }
  /**
   * The panel has no Note open, so there is no Scratchpad to re-key and no text to
   * put at the adopted id. Nothing was written and nothing changed.
   */
  | { kind: 'unopened' }
  /**
   * No Note at the adopted id has been *proven* — the write did not land, the read-back
   * failed, or it answered with a Note whose body this panel cannot account for, which
   * is somebody else's Note and not evidence of anything this pass did — **so the panel
   * is back on the id it was on.**
   *
   * The undo is the panel's, in the one place that moves its id
   * (`NotesState.openScratchpadId`), and it is what this outcome is worth relying on:
   * nothing may be recorded, and the next pass reads the id it has always read, finds
   * the body it has always found there, and makes the same move again. Left moved, that
   * next pass instead reads an id holding either nothing or a descendant of the server's
   * body, and the second of those is a Pending Conflict nothing can clear (#7) over an
   * ordinary push. The save path still has the write queued and still retries it, and what
   * lands at the abandoned id afterwards is this session's text over whatever was there —
   * nothing, or a Note this device had abandoned at that id, never one anything opens
   * (`ScratchpadMove` in `app-store.tsx` states that trade). `pair` works through every
   * ordering, and no Scratchpad text is lost in any of them.
   */
  | { kind: 'unwritten' }

/**
 * What came of asking the panel to take a Jot Server's body.
 *
 * Three answers and not two, because a refusal is the interesting one: the guard's
 * verdict decides whether this pass records a Conflict, waits, or reports that
 * nothing could be established (`applyPull`). Carrying the verdict out rather than
 * collapsing it to a boolean is the whole reason the guard is a named decision — a
 * `false` here would have the engine guess which of three states it was in, and one
 * of the three writes a Pending Conflict that nothing can clear yet (#7).
 */
export type PullOutcome =
  /**
   * Written and read back holding bytes the panel put there. This is the Note now at
   * the pulled id — the pulled body, or the keystroke that beat it to the write.
   */
  | { kind: 'applied'; note: Note }
  /** The guard said no, and this is which of its answers it gave. */
  | { kind: 'refused'; because: Exclude<PullVerdict, 'apply'> }
  /**
   * The guard said yes and no Note at the id has been *proven* — the write did not
   * land, the read-back failed, or it found a Note holding bytes the panel never put
   * there. The same answer `adoptScratchpad` gives, for the same three reasons and with
   * the same guarantee behind it: the panel is back on the id it was on, because the one
   * place that moves it undoes a move it could not commit.
   *
   * The editor is *not* undone with it, and the asymmetry is the point. The editor is
   * left holding the pulled body, which `PanelText.appliedPull` keeps the next pass from
   * mistaking for something the user typed; an id left naming a Note the panel never
   * wrote — or none at all — has nothing that could recognise it.
   */
  | { kind: 'unwritten' }

/**
 * Which body the user kept (ADR-0002). The other one is discarded outright.
 */
export type Resolution = 'local' | 'server'

/**
 * A Jot Server's body, on its way over the Scratchpad because the user said so.
 *
 * It is deliberately not a `PullTarget`, and the difference is the whole reason
 * `applyResolvedBody` exists beside `applyPulledBody`. A pull is something this module
 * decided, so `guardPull` makes it prove the editor has not moved since the body it
 * was judged against — and in a Conflict that proof is false by construction, because
 * "both sides changed" is what a Conflict *is*. Running a Resolution through that
 * guard would refuse every one of them.
 *
 * What the guard protects is text the user never agreed to lose. Here they agreed:
 * they were shown both bodies and pressed a button whose label says the other one
 * goes. So the authorisation comes from the click rather than from a comparison, and
 * the panel's remaining job is ordering — a Resolution still goes in through the
 * ordinary save path, so it cannot land under a write that was already queued.
 */
export interface ResolutionTarget {
  /** The Note the panel is on, and the id the body is written at. */
  id: NoteId
  /** The Jot Server's body, as read back at `rev` in the same pass that writes it. */
  body: string
}

/**
 * What came of asking the panel to take the body the user kept.
 *
 * Two answers rather than the three a pull has, because a Resolution has no refusal:
 * the guard that produces `PullOutcome.refused` is not consulted (`ResolutionTarget`).
 * What is left is the same proof a pull needs — a Note read back holding bytes the
 * panel put there — because an agreement recorded over a write that never landed is
 * the one way a Resolution could lose the body it was told to keep *and* the one it
 * was told to discard.
 */
export type ResolutionOutcome =
  /** Written and read back. The Scratchpad now holds the body the user kept. */
  | { kind: 'applied'; note: Note }
  /** Not proven: the write did not land, or the read-back did not show it. */
  | { kind: 'unwritten' }

/**
 * What storage holds at the Scratchpad id: the body, nothing at all, or an answer
 * this device could not get. See `readScratchpad` for why all three are needed and
 * why two of them must never be confused for one another.
 */
type LocalScratchpad =
  { kind: 'body'; body: string } | { kind: 'absent' } | { kind: 'unreadable' }

/** What is left of it once `runOnce` has ended the pass over an unreadable one. */
type ReadableScratchpad = Exclude<LocalScratchpad, { kind: 'unreadable' }>

/**
 * The local read as the guard has to see it, with the absence kept as an absence.
 *
 * One conversion rather than a ternary at each call site, because this is the exact
 * step that was wrong: an absent Note handed over as `''` is a body the panel has
 * never held, so the guard read a panel still showing the removed file's text as the
 * user having typed, and the restore ADR-0002 requires came out as a Pending Conflict
 * — including over two bodies that were byte identical. `agreedBody` is what makes the
 * absence answerable at all, and it is never a body to write or to push: see
 * `ComparedScratchpad`.
 */
function comparedWith(
  local: ReadableScratchpad,
  agreedBody: string | null,
): ComparedScratchpad {
  return local.kind === 'body'
    ? { kind: 'body', body: local.body }
    : { kind: 'absent', agreedBody }
}

/**
 * A push as it was made: the Note id it was made at, the body it sent, and the
 * Last-Agreed Body it was measured against.
 *
 * One value rather than three parameters, because the last two are both bodies and
 * would sit next to each other: transposed at a call site they would compile, and
 * `recordPush` would read every `409` as the answer to the other question — an
 * ordinary push recorded as a Conflict, or a Conflict pushed straight over.
 */
interface Pushed {
  id: NoteId
  /** The exact bytes sent, one of the two bodies a `409`'s record is compared against. */
  body: string
  /**
   * The body the Jot Server held when the two last agreed, which is the other.
   * Every call site knows it, and knows it exactly: the recorded agreement, the
   * record Pairing is working from — whose body is the one the two demonstrably had
   * in common — or `''` for a server that has never been pushed to, which holds no
   * text a push could discard. A record that cannot supply it is not an agreement
   * (`asLastAgreed`), and this slice pairs instead of guessing.
   */
  agreedBody: string
}

export interface SyncEngineDeps {
  /** Read fresh on every run, so a token pasted a moment ago is the one used. */
  settings: Pick<SettingsStore, 'get'>
  syncState: SyncStateStore
  /**
   * `get`, and nothing wider — read-only note storage, unchanged by pull.
   *
   * It would have been the natural place to put the pulled write, and that is the
   * trap: a store that can write puts a Jot Server's body one line away from every
   * comparison in this module, with nothing but a convention between them. Both
   * writes are the panel's instead (`adoptScratchpad`, `applyPulledBody`), which is
   * not only where the guard can be evaluated but the reason it *has* to be — so the
   * narrowing and the correctness argument are the same fact rather than two.
   */
  notes: Pick<NotesStore, 'get'>
  /** This device's default label; `SyncSettings.deviceLabel` overrides it. */
  info: Pick<PlatformInfo, 'deviceLabel'>
  /**
   * The Note the panel currently has open: the id a keystroke would land at, and the
   * only answer this module asks for or acts on.
   *
   * Not a fallback for a device that has not paired yet, which is what it used to be.
   * Sync State's `scratchpadId` records which Note this device has *agreed* with a Jot
   * Server about, and consulting that first — `state.scratchpadId ?? openScratchpadId()`
   * — meant the authoritative answer was read only when there was no recorded one, which
   * is exactly the case where a disagreement between the two is invisible. On a paired
   * device it decided a whole pass about a Note the panel was not on, and the guard then
   * refused every pull as `unopened` for the life of the process, because nothing else
   * talks the two round. What a disagreement means, and how a pass finishes it, is in
   * `runOnce`.
   *
   * It has to be the id the panel would put a keystroke at rather than the last one it
   * managed to read back from storage — the same id `applyPulledBody`'s guard checks
   * `comparedId` against, so that a pass and the guard it hands that id to are answering
   * about one Note (`NotesState.openScratchpadId`).
   */
  openScratchpadId: () => NoteId | null
  /**
   * Have the panel take on `id` as its Scratchpad, and put this session's text
   * there.
   *
   * Both halves, because either alone strands text. Re-keying storage without the
   * panel following leaves the session's keystrokes landing at an id the next
   * launch never opens; moving the panel without writing the Note leaves the
   * recorded identity naming storage that holds nothing.
   *
   * The body is deliberately not a parameter. The bytes that belong at the adopted
   * id are the ones the editor holds when the id moves, and asking the panel for
   * them is the only way to get a value that cannot already be stale — anything
   * this module passed in would have been read before a network round trip.
   */
  adoptScratchpad: (id: NoteId) => Promise<AdoptOutcome>
  /**
   * Have the panel write a Jot Server's body over the Scratchpad — if the guard lets
   * it (`./pull-guard`).
   *
   * The guard and the write are one call and not two, for the reason
   * `adoptScratchpad` gives: asking "may I?" and then writing leaves a gap a
   * keystroke fits into, and the keystroke is the thing being protected. The panel
   * reads its own text, decides, and commits without awaiting in between, so the
   * bytes the decision was made about are the bytes that were still there.
   *
   * Here the body *is* a parameter, which is the mirror of the rule above and the
   * same rule: the value that must not be stale is the one being written, and this
   * one comes from the server. What could be stale is the body it is judged against,
   * which is why that body travels with it (`PullTarget.comparedBody`) instead of
   * being read again on arrival.
   */
  applyPulledBody: (target: PullTarget) => Promise<PullOutcome>
  /**
   * Have the panel write the body the user kept over the Scratchpad — no guard.
   *
   * Separate from `applyPulledBody` rather than a flag on it, because the two differ
   * in who authorised the write, and that is exactly the fact a flag would hide at
   * the one call site where getting it wrong destroys text. `guardPull` is the
   * sync layer's own restraint; a Resolution is the user's instruction, and
   * `ResolutionTarget` says why the guard cannot be asked about one.
   *
   * The body is a parameter for the reason `applyPulledBody` gives, and one more:
   * it is the body read back from the server in this same pass, so it is the one the
   * agreement recorded alongside it names.
   */
  applyResolvedBody: (target: ResolutionTarget) => Promise<ResolutionOutcome>
}

export interface SyncEngine {
  /**
   * Pair if needed, push if there is something to push, pull if there is not, and
   * report what happened.
   *
   * Which of the three it is remains this module's decision rather than the
   * caller's, and pull did not change that: a pass with local changes pushes, a pass
   * with none asks. So the panel-open and interval triggers pull because there is
   * nothing else for them to do, and a settled save pushes because there is — no
   * trigger has to say which it wants, and none can be wrong about it.
   *
   * Serialised: a trigger arriving mid-run is coalesced into exactly one further
   * pass, because two triggers mean the same thing — look again — and overlapping
   * pushes would race for the same revision.
   */
  sync(): Promise<SyncStatus>
  /**
   * Resolve the Pending Conflict by keeping one body and discarding the other
   * (ADR-0002), then put the kept body and the server back in agreement.
   *
   * Serialised with `sync` through the same queue, because the two write the same
   * Sync State and a pass that ran between reading the conflict and clearing it
   * would be deciding from a record about to stop existing.
   *
   * What it does *not* do is trust the Pending Conflict's revision. That number was
   * true when the Conflict was recorded and a Conflict can sit for days, so a
   * Resolution re-reads the Jot Server first and aims at what it says now. Aiming at
   * the recorded revision instead is a `409` and a fresh Conflict — the user presses
   * a button and nothing visibly changes, which is the failure this whole pane exists
   * to end.
   */
  resolve(choice: Resolution): Promise<SyncStatus>
}

export function createSyncEngine(deps: SyncEngineDeps): SyncEngine {
  let inFlight: Promise<SyncStatus> | null = null
  let queued: Promise<SyncStatus> | null = null

  /**
   * A pulled body this pass authorised and could not prove had landed, or null.
   *
   * The one thing storage can hold that this device did not type. `applyPulledBody`
   * queues the pulled body through the ordinary save path, so a `flush` that fails
   * requeues it and retries two seconds later on its own timer — after this pass has
   * already recorded nothing. When that retry lands, storage holds a body that differs
   * from the Last-Agreed Body for a reason that is not a local change, and the next
   * pass used to push it: bytes this device only ever received, aimed at a revision the
   * server had already passed. A server that had moved again answered the `409` with a
   * third body, and `recordPush` recorded a Conflict over a body the user never touched.
   *
   * Kept in memory rather than in Sync State, because the window is one process's: the
   * retry, the `saved` that follows it and the pass it triggers are all this session.
   * The cost is the ordering that leaves the session — a quit inside those two seconds
   * drains the queue synchronously (`beforeunload`), so the bytes land with no memory of
   * where they came from and the next launch pushes them. That push is what every
   * version of this code did, and it is one request and a `409` that resolves to an
   * agreement unless the server moved twice; persisting provenance would mean a fourth
   * record in Sync State for it, which is not a trade this defect justifies.
   *
   * Held while storage holds those bytes *or* the ones they are still queued over, and
   * no agreement names them. Both halves are the window: the retention test used to ask
   * only whether storage held the pulled bytes, which is false for as long as the write
   * has not landed — the very state this exists for — so any pass in between forgot the
   * pull and the pass after the landing pushed it. What it may not survive is storage
   * holding a *third* body: that is a write nobody here queued, so the bytes are the
   * user's, and licensing a Jot Server's body over them is the one way this memory could
   * destroy anything. `runOnce` clears it.
   */
  let unprovenPull: {
    id: NoteId
    body: string
    /**
     * The pre-pull Scratchpad: what storage held at `id` when the pull was authorised.
     *
     * It is what tells "the queued write has not landed yet" apart from "something else
     * wrote", which are the same fact about storage — a body that is not the pulled one —
     * and opposite facts about provenance. Where it comes from is `applyPull`'s
     * `queuedOver`.
     *
     * `null` for two states that need the same answer: no Note there at all, and a write
     * that has already landed. Neither leaves anything the pulled bytes are still on
     * their way over, so the only body that may excuse them is the pulled body itself,
     * and any other reading is the user having typed.
     */
    queuedOver: string | null
  } | null = null

  /**
   * A write that does not land is reported rather than ignored: after an accepted
   * push it means the agreement is a revision behind, so the next push will meet a
   * `409` and raise a Conflict the user did not cause. Losing the report as well
   * would leave that unexplained.
   *
   * Which is a claim about the stores, not just about this function, and both of
   * them keep it: `sync-state-store.ts` and `writeSyncState` throw rather than
   * swallow, alone among Jot's persistence, and each says why.
   */
  async function write(patch: Partial<SyncState>): Promise<boolean> {
    try {
      await deps.syncState.set(patch)
      return true
    } catch (error) {
      console.error('jot: could not record the sync state', error)
      return false
    }
  }

  /**
   * The Scratchpad as storage answers for it, in the three answers that are
   * genuinely different.
   *
   * This used to be a body or null, and null carried both of the failures below
   * because both stores collapsed every read failure into one. Issue #12 is what
   * separated them — `readNote` throws on EACCES/EIO and returns null only for
   * genuine absence, and the web store rejects a failed transaction while answering
   * null for an absent record or a Tombstone — and pull is what needs the difference:
   *
   * - **absent** is a fact, and absence is never an intent to delete (ADR-0002). A
   *   Jot Server holding a body is what a wiped notes folder is restored from, which
   *   is the whole of what makes reinstall-and-resync work.
   * - **unreadable** is text that is there and out of reach. Treating it as absent
   *   would have a server's body written straight over it, and `atomicWrite`
   *   publishes by renaming over the target, so the local copy would be gone — the
   *   data-loss path issue #12 exists to close, arriving from the other direction.
   *
   * Neither is ever pushed as `''`, which is the other half of the same rule: an
   * empty push replaces the Scratchpad on every paired device with nothing.
   *
   * The persisted body rather than the editor's live text on purpose, in both
   * directions. What the Jot Server relays should be what the local source of truth
   * holds; and what a pull is judged against has to be a value the sync layer can
   * hold still across a round trip, which the editor's text is not (`./pull-guard`).
   */
  async function readScratchpad(id: NoteId): Promise<LocalScratchpad> {
    try {
      const note = await deps.notes.get(id)
      return note === null ? { kind: 'absent' } : { kind: 'body', body: note.body }
    } catch (error) {
      console.error('jot: could not read the scratchpad for sync', error)
      return { kind: 'unreadable' }
    }
  }

  /**
   * Record that this device and the Jot Server hold `pushed.body`, at the revision
   * the server has it at.
   *
   * `scratchpadId` is written together with the agreement and never before it:
   * an id recorded for a push that never landed would pin this device's identity
   * to a Note the server has never heard of.
   *
   * Never *without* it either, and that is the other half of the same rule. Every site
   * that records an agreement records the identity in the same write — here, `pair`,
   * `applyPull`, and the converged read in `runOnce` — because the id and the body are
   * one fact: this device and the server hold this text, at this Note. An agreement
   * written on its own is what would let a move the panel committed go unrecorded for
   * ever (`runOnce`). A Conflict carries the identity too, for a different reason:
   * `recordConflict`.
   */
  async function recordAgreement(pushed: Pushed, rev: number): Promise<SyncStatus> {
    const stored = await write({
      scratchpadId: pushed.id,
      lastAgreed: { rev, body: pushed.body },
    })
    if (!stored) return { kind: 'unavailable', what: 'sync-state' }
    return { kind: 'agreed', rev, pushed: true }
  }

  /**
   * Record a Pending Conflict, and with it the Note the panel is on.
   *
   * The Last-Agreed Body is left exactly as it was at all three sites that reach here,
   * because the patch never names it. Where there is one it is untouched for a reason —
   * this device and the server did agree at that revision, and it is what a Resolution
   * pushes against — and at the Pairing site there is none, which is why #7 has to work
   * from the record's body there. Only the Conflict is new, and the identity, which is
   * the half this used to leave behind.
   *
   * `scratchpadId` travels with a Conflict for the opposite reason it travels with an
   * agreement. There the id may not be recorded *before* the body is proven; here there
   * is nothing further to prove and no later pass to prove it in, because `runOnce`
   * returns at the top for as long as a Pending Conflict is recorded — so this is the
   * last pass this device makes until a Resolution (#7), while every launch in between
   * bootstraps from whatever id is on record. A device whose panel had committed a move
   * and not had it recorded therefore kept a stale one permanently, and the next launch
   * opened the Note the session was *not* in — no list, no switcher and no search
   * (ADR-0001), so the text was unreachable rather than merely unsynced. That is what
   * turns "sync stops until #7" into a loss, and the write costs nothing to widen.
   *
   * The id is the panel's own at each site and never `record.id`: which id wins is part
   * of what a Resolution decides, and what has to be reachable meanwhile is the Note this
   * device has the text in. Each site's id has a Note behind it — a body just read from
   * it, or one the panel proved when it moved there — so bootstrap opens a Scratchpad
   * rather than a placeholder.
   */
  async function recordConflict(
    record: ScratchpadRecord,
    scratchpadId: NoteId,
  ): Promise<SyncStatus> {
    const stored = await write({ scratchpadId, pendingConflict: record })
    if (!stored) return { kind: 'unavailable', what: 'sync-state' }
    return { kind: 'conflict', record }
  }

  /**
   * Record an accepted push, or the Conflict that displaced it.
   *
   * `reaimed` is the one-shot budget for the re-aim below, and the reason there is a
   * budget at all is that the answer it acts on is a server's: a `409` on every
   * attempt would otherwise hold this pass open pushing for ever, where every other
   * path here costs at most two requests.
   */
  async function recordPush(
    client: ScratchpadClient,
    outcome: PushOutcome,
    pushed: Pushed,
    reaimed = false,
  ): Promise<SyncStatus> {
    if (outcome.kind === 'accepted') return recordAgreement(pushed, outcome.rev)

    if (outcome.kind === 'conflict') {
      const record = outcome.record
      /*
       * A `409` says the revision moved. It does not say the body changed, and a
       * Conflict is the state where the body changed on *both* sides since the two
       * last agreed (CONTEXT.md) — so what the record's body is measured against is
       * the two bodies that answer that, and a Conflict is what is left when it is
       * neither of them.
       *
       * The bytes just pushed, which say the push landed and the answer was lost. A
       * `PUT` that commits and whose answer never arrives — a dropped connection, or
       * `REQUEST_TIMEOUT_MS` firing after the commit — is reported as `unreachable`
       * and records nothing, so the next ordinary pass pushes the same body against
       * the revision it agreed at and meets the `409` its own commit created.
       *
       * The Last-Agreed Body, which says the server has not moved since the
       * agreement and only this device has: an ordinary push, aimed at a revision
       * that had since moved. Two ordinary pushes reach it and no user error does — a
       * character typed on another device and undone leaves the server holding the
       * agreed body again, two revisions on, and a device that never saw either pushes
       * against the revision it still agrees at. Measuring the record against the
       * pushed bytes alone called that a Conflict over a body this device had itself
       * agreed to, and stopped sync for good (ADR-0002; #7 is what clears one),
       * escapable only by deleting `sync-state.json` by hand.
       *
       * The two cannot both hold, so their order is not a precedence: every call site
       * pushes only a body that differs from the one it agreed on — `runOnce` returns
       * `agreed` before pushing an unchanged body, and `pair` pushes only what differs
       * from `record.body`, which is the body it passes as agreed.
       *
       * One rule and not a list of special cases. A device holding nothing, and a
       * server holding `""`, were each patched in the Pairing analysis below where
       * they surfaced; every one of them is the same question — has the body actually
       * changed on both sides? — and it is asked here, at the point a Pending Conflict
       * is written, so every place that writes one is a place that establishes it.
       * `applyPull` is the third and answers the same question from the same two
       * bodies, with the editor's live text standing in for "mine".
       *
       * The bodies and nothing else: the id the record names is not part of the
       * question, because a Conflict is about the body, and equal bodies filed under
       * two ids are still nothing to resolve. Which is also why both are tested ahead
       * of `isNoteId` below — that check guards the *record* this device is about to
       * persist, and neither of these persists one, only this device's own id and a
       * revision `asRecord` has already held to being a revision. Asking about the id
       * first would leave a device that met a `409` carrying an odd id re-pushing the
       * same body for ever.
       *
       * Compared, never written. `record.body` is measured and dropped: what
       * `recordAgreement` writes is the body this device sent — which is what keeps
       * every Last-Agreed Body in Sync State bytes this device holds. Pull does not
       * change that rule, it satisfies it the long way round: a pulled body is recorded
       * only after the panel has written it and a Note has been read back holding it
       * (`applyPull`), so it is bytes this device holds by the time it is recorded.
       */
      if (record.body === pushed.body) return recordAgreement(pushed, record.rev)

      if (record.body === pushed.agreedBody) {
        // Aimed again at the revision the `409` named, in this pass rather than left
        // to a later one, for the reason `pair` gives: nothing schedules a later pass,
        // so deferring the push is how the local text reaches the server never.
        // Nothing is recorded first — an agreement written at the older revision and
        // then not replaced would have the next pass push against a revision the
        // server has moved past.
        //
        // The budget spent, and nothing to show for it: two ordinary pushes lost the
        // same race, so the attempt failed and nothing about the Scratchpad was
        // established. There is no Conflict here to record — the record's body is one
        // the two sides demonstrably had in common — and an agreement would name a
        // revision this device never reached, so the `409` is reported as the attempt
        // it was and the next settled save asks again.
        if (reaimed) return { kind: 'rejected', httpStatus: 409 }
        return recordPush(
          client,
          await client.push(pushed.body, record.rev, pushed.id),
          pushed,
          true,
        )
      }

      // The same check the Pairing path makes on the id it is about to adopt, for
      // the same reason and to keep the two in step: a record Sync State would
      // refuse is one to report rather than one to try to write.
      if (!isNoteId(record.id)) return { kind: 'malformed' }

      // Named with the id this push was made at, which is the Note the panel is on: the
      // one `runOnce` read the body from, or the one `pair` proved a Note at before it
      // pushed. Why a Conflict records it at all is `recordConflict`.
      return recordConflict(record, pushed.id)
    }

    return outcome
  }

  /**
   * Pairing: this device takes the Jot Server's Scratchpad id as its own
   * (ADR-0001), for the cases where `record.body` is a body the two sides have in
   * common — see `runOnce` for which those are and why they are one rule.
   *
   * What that establishes is a fact about the past: at revision `record.rev` the Jot
   * Server held `record.body`, and this device held it too or held nothing but text
   * grown on top of it. That fact is what authorises adopting the id and what is
   * recorded as the Last-Agreed Body, and neither use expires — later typing cannot
   * make it untrue that the two once agreed.
   *
   * What *is* stale by the time this runs is `localBody` as a *value to write*. It
   * was read before the round trip, so anything typed since is missing from it, and
   * writing it at the adopted id would put text on disk that no longer exists
   * anywhere else. So it is never written. The panel writes the id, from the body
   * it is holding at the instant the id moves, and reports the Note that resulted;
   * `localBody` is only ever compared and pushed.
   *
   * That also means the local body having moved on is not a reason to abort. It
   * cannot be one: a body typed on top of `record.body` is a *descendant* of the
   * server's, so only one side has changed since the two last agreed, and by
   * CONTEXT.md that is not a Conflict — it is an ordinary push. Abandon the pass
   * instead and the next one finds a local body differing from the server's with no
   * agreement on record, raises a Pending Conflict nothing in this slice can clear,
   * and stops pushing. Refusing to pair is the more cautious-looking option and the
   * one that loses.
   *
   * Every ordering, including the process dying between any two steps:
   *
   * - Refused before anything moved (`unopened`). Storage, Sync State and the panel
   *   are untouched, which is what makes a half-completed Pairing indistinguishable
   *   from one that never started. Next launch: `list[0]`, the local Scratchpad.
   * - Panel committed, the write not landed (`unwritten`). Storage is exactly as it
   *   was, Sync State names nothing, and `list[0]` is still the local Scratchpad
   *   holding text identical to what the panel has. The save path has the write
   *   queued at the adopted id and retries it on its own timer; `beforeunload` drains
   *   the same queue synchronously, so a clean quit lands it too. Killed here, the
   *   panel's move is simply forgotten — it was only ever in memory.
   * - Note written at the adopted id, Sync State not yet naming it — either because
   *   the read-back failed (`unwritten` again) or because the process died between
   *   the two. The hazardous window in the first direction, and it is safe because of
   *   *what* is at that id: the panel's own live text, stamped `updatedAt = now`, so
   *   it is `list[0]` and the next launch opens it. The two previous attempts at this
   *   defect put an *empty* Note here, which is the same window and a total loss.
   * - Note written and pushed, Sync State naming neither. The push landed, so the
   *   server holds this device's body at a new revision; nothing here is recorded, so
   *   the next pass pairs again from a `GET` — and finds the two sides holding the
   *   same body, which is the converged case below. An honest retry, which is the
   *   whole reason there is one Sync State write per pass and not two.
   * - Sync State naming the adopted id with no Note there — the hazardous window in
   *   the other direction, where bootstrap opens an empty placeholder while the text
   *   sits at an id nothing reopens. Unreachable: nothing here is written until the
   *   panel has handed back a Note it read out of storage.
   * - Recorded, then killed. Paired. The old Note stays behind holding the same
   *   bytes, older, and nothing opens it — one duplicate, no text at risk.
   */
  async function pair(
    client: ScratchpadClient,
    record: ScratchpadRecord,
    openId: NoteId,
    localBody: string,
  ): Promise<SyncStatus> {
    // What is at the Scratchpad id once this device is paired, and so what a push has
    // to send. `localBody` while the id does not move; the Note the panel handed back
    // when it does, because that one was read out of storage holding bytes the panel put
    // at the adopted id, where `localBody` was read before the round trip. The provenance
    // is the load-bearing half and not the freshness: a read-back that merely found *a*
    // Note there is a body no device holds as its Scratchpad, and this line is what would
    // push it (`ScratchpadMove` in `app-store.tsx`).
    let body = localBody

    // The same id already — a device re-pairing after losing its Sync State. No Note
    // moves and none is written: only the agreement was missing.
    if (record.id !== openId) {
      // The compare-and-swap: the panel checks what it holds and moves in one
      // synchronous step, so nothing can be typed into a gap between deciding and
      // committing, and the bytes written are current by construction rather than by a
      // comparison against a body read before the round trip.
      const adopted = await deps.adoptScratchpad(record.id)
      if (adopted.kind !== 'adopted') return { kind: 'unavailable', what: 'scratchpad' }
      body = adopted.note.body
    }

    // Paired and converged: record the agreement, send nothing. The body recorded is
    // this device's own, which at this point is the same text — `record.body` is
    // compared here and written nowhere, exactly as in `recordPush`.
    if (body === record.body) {
      const stored = await write({
        scratchpadId: record.id,
        lastAgreed: { rev: record.rev, body },
      })
      if (!stored) return { kind: 'unavailable', what: 'sync-state' }
      return { kind: 'agreed', rev: record.rev, pushed: false }
    }

    // Paired and ahead of the body the two agreed on: an ordinary push against the
    // revision they agreed at, made in this pass rather than left to a later one.
    // Nothing schedules a later one on its own — a settled save is what triggers a
    // pass, and a device that has just paired may not save again for hours — so
    // deferring the push is how the local text reaches the server never.
    //
    // `recordPush` writes the identity together with the new agreement, which is why
    // nothing is written here first. Writing the older agreement and then failing to
    // write this one would leave the device claiming agreement at a revision the
    // server has moved past: the next pass would push against it, meet a `409` and
    // raise a Conflict the user did not cause. With nothing recorded, the next pass
    // re-reads and lands in the converged case above.
    return recordPush(client, await client.push(body, record.rev, record.id), {
      id: record.id,
      body,
      // The body the two sides demonstrably had in common at `record.rev`, which is
      // the whole of what Pairing may call agreed: either the same text, or an empty
      // server every local body descends from. A `409` here is measured against it,
      // so the revision moving between the `GET` and this `PUT` — including while the
      // panel was adopting the id above — is an ordinary push and not a Conflict.
      agreedBody: record.body,
    })
  }

  /**
   * Pull: take the Jot Server's body, or record why it was not taken.
   *
   * The direction that can destroy work, so nothing here decides anything about the
   * editor. The panel does, in one synchronous step against its own text
   * (`./pull-guard`), and this function's whole job is turning the answers it can give
   * into the four things Sync State may say — which is where the guard stops being a
   * safety check and becomes the Conflict predicate:
   *
   * - **applied.** The two now hold the same body, so record the agreement. This is
   *   the first Last-Agreed Body in the project's history whose bytes came off the
   *   wire, and the invariant it seems to break survives intact: what is recorded is
   *   the body read back out of *storage*, so it is still bytes this device holds. It
   *   has to be, or the record would claim an agreement over text no local Note has.
   * - **ahead.** The editor holds something neither storage nor a pull put there, so
   *   the local body changed since the two last agreed — and the server's changed
   *   too, or this pass would have had nothing to pull. Both sides, since the
   *   agreement: the Conflict as CONTEXT.md defines it, established from bodies at the
   *   third and last place one is written, never from a status code — and now never
   *   from an absent local read either, which is what `comparedWith` is for.
   * - **queued.** A save is still going in and nothing is proven either way. Waiting:
   *   nothing recorded, nothing written, asked again next pass.
   * - **unopened / unwritten / unpersisted.** The panel had nothing to compare, the
   *   write is not proven to have landed, or storage holds no Note while the editor
   *   holds the only copy of its text. The first two are what `pair` reports for the
   *   same two cases, and all three are the same answer for the same reason: nothing is
   *   recorded, and the save path's retry or the next keystroke heals it without the
   *   engine doing anything.
   *
   * `comparedId` is the id the local body was read from, which is not always
   * `record.id` — a device pairing to a server that is ahead of it is judged on the
   * Note it has open and writes at the Note the server named, in the one step. The
   * guard is what refuses if the panel has since moved off it.
   *
   * Pairing is the usual reason the two differ and not the only one: a paired device
   * whose server has since been re-keyed by another device re-keys with it, from here,
   * and the `scratchpadId` written below moves. That is the same trade `pair`'s last
   * ordering describes — the old Note keeps its bytes, nothing reopens it, one
   * duplicate and no text at risk — and it is safe for the same reason it is safe
   * there: the guard has held, so the bytes left behind are bytes the server has too.
   */
  async function applyPull(
    record: ScratchpadRecord,
    comparedId: NoteId,
    compared: ComparedScratchpad,
  ): Promise<SyncStatus> {
    // The same check `pair` and `recordPush` make, ahead of anything that writes: an
    // id Sync State would refuse is one to report rather than one to adopt — and here
    // it also guards a Note about to be created at it.
    if (!isNoteId(record.id)) return { kind: 'malformed' }

    /**
     * Whether this pull also re-keys the Scratchpad — and so whether the two exits below
     * that lean on `queuedOver` have anything worth remembering.
     *
     * Those two are claims about bytes that are still on their way to storage, measured
     * against what was read at `comparedId`, and a re-keying pull leaves the panel on that
     * id in neither of them. A write that could not be proven has had the move undone
     * before this returns (`PullOutcome`), so the pulled bytes are on their way to an id
     * nothing reopens and no later pass reads. A write that was proven has moved the panel
     * with it, which abandons the id they came from. Either way the first retention test to
     * look drops the memory, which is why the `null` that used to stand in for "this pass
     * never read that id" is gone rather than corrected: it was answering a question no
     * pass asks.
     *
     * The third exit — a write that landed, at the id the panel is now on, whose agreement
     * could not be recorded — is deliberately not gated on this. Nothing there is still on
     * its way, so `queuedOver` has no part in it and `record.id` is the panel's own id
     * whether or not the pass re-keyed.
     */
    const rekeys = comparedId !== record.id
    /** What storage held at the id the pulled bytes are queued at; `null` for no Note. */
    const queuedOver = compared.kind === 'body' ? compared.body : null

    const applied = await deps.applyPulledBody({
      id: record.id,
      body: record.body,
      comparedId,
      compared,
    })

    if (applied.kind === 'refused') {
      if (applied.because !== 'ahead') {
        return applied.because === 'queued'
          ? { kind: 'waiting', what: 'pull', record }
          : { kind: 'unavailable', what: 'scratchpad' }
      }

      // `comparedId` and not `record.id`: the guard refused, so nothing moved and no Note
      // was written — the panel is on the id this pass read, and `ahead` is only reachable
      // from a body that was read there (`guardPull`), so it is an id with a Note at it.
      return recordConflict(record, comparedId)
    }

    // Authorised, and the write not proven to have landed — so from here on the pulled
    // body may still reach storage without this pass, and `unprovenPull` is what keeps
    // the next one from mistaking it for something the user typed. Both exits below are
    // that same state: `flush` reports a failed write by requeueing it and retrying on
    // its own timer, and the read-back therefore finds either nothing new or the Note
    // storage still holds.
    //
    // Which is what the memory needs `queuedOver` for: nothing is proven about the pulled
    // bytes by a pass that reads them absent, so what has to be recognisable instead is
    // the Scratchpad they have not replaced yet.
    if (applied.kind === 'unwritten') {
      if (!rekeys) unprovenPull = { id: record.id, body: record.body, queuedOver }
      return { kind: 'unavailable', what: 'scratchpad' }
    }

    // Read back out of storage and compared, not assumed. Two ways to be here and the
    // read-back is what tells them apart, because it is a fresh read of the id the write
    // went to: it holds what the pull was queued over, which is `unwritten` with a Note to
    // prove it — or it holds something else, which is a keystroke that landed between the
    // guard and the flush winning the write, because the save path queues the whole body.
    // Neither may record an agreement: that would claim the server holds bytes it has
    // never seen.
    //
    // Only the first is a pull still on its way to disk, and only it is remembered. A
    // keystroke replaces the queued body under the same key, so after the second the pulled
    // bytes are not going anywhere, and claiming provenance over what is there would excuse
    // the *user's* text from being pushed on every later pass.
    if (applied.note.body !== record.body) {
      if (!rekeys) {
        unprovenPull =
          applied.note.body === queuedOver
            ? { id: record.id, body: record.body, queuedOver }
            : null
      }
      return { kind: 'unavailable', what: 'scratchpad' }
    }

    // The identity and the agreement in one write, as `recordAgreement` does it and for
    // the same reason: an id recorded without the agreement pins this device to a Note
    // the server has not been shown to hold.
    const stored = await write({
      scratchpadId: record.id,
      lastAgreed: { rev: record.rev, body: applied.note.body },
    })
    if (!stored) {
      // The write *landed* and the agreement naming it did not, which is the one exit
      // where storage holds a Jot Server's body with nothing on record about where it
      // came from. Exactly what `unprovenPull` is for — "these bytes came from the server
      // and no agreement names them yet" — and it was the one exit that did not say so,
      // so every later pass pushed a body this device only ever received, and one
      // ordinary edit elsewhere turned the `409` into a persisted Pending Conflict over
      // two bodies the user never touched.
      //
      // `record.id`, because the move was proven and the panel is on it. And nothing to
      // be queued over: these bytes are in storage, so the only thing that can replace
      // them is a write nobody here made, which is the user typing — and the retention
      // test drops the memory the moment storage stops holding them.
      unprovenPull = { id: record.id, body: record.body, queuedOver: null }
      return { kind: 'unavailable', what: 'sync-state' }
    }
    return { kind: 'agreed', rev: record.rev, pushed: false }
  }

  async function runOnce(): Promise<SyncStatus> {
    let syncSettings: SyncSettings
    try {
      syncSettings = (await deps.settings.get()).sync
    } catch (error) {
      // Settings are reached over IPC on desktop. Unreachable settings are
      // indistinguishable from settings that might say "off", so they say it.
      console.error('jot: could not read the sync settings', error)
      return { kind: 'off' }
    }

    const { enabled, endpoint, token, deviceLabel } = syncSettings

    // Sync is off by default, and off means off: no request, no Sync State
    // write, nothing to distinguish this build from the one before it.
    if (!enabled || token === '') return { kind: 'off' }

    // Checked here, before a client exists, so that "the endpoint was refused"
    // and "the request failed" can never be the same code path (ADR-0003).
    const resolved = scratchpadEndpoint(endpoint)
    if (!resolved.ok) {
      // An endpoint nobody has filled in yet is not a misconfiguration to report,
      // it is the same answer as a missing token: this device is not set up.
      return resolved.problem === 'missing'
        ? { kind: 'off' }
        : { kind: 'refused', problem: resolved.problem }
    }

    let state: SyncState
    try {
      state = await deps.syncState.get()
    } catch (error) {
      // Unlike the bootstrap path, this must not fall back to "unpaired": a
      // device that cannot read its Sync State would re-pair, and re-pairing
      // pushes against revision 0.
      console.error('jot: could not read the sync state', error)
      return { kind: 'unavailable', what: 'sync-state' }
    }

    // A Conflict is the user's to resolve, and until they do, sync waits —
    // silently, and without a request. Editing carries on as normal (ADR-0002).
    if (state.pendingConflict !== null) {
      return { kind: 'conflict', record: state.pendingConflict }
    }

    /*
     * The one Note this pass is about, and there is one because there is one place that
     * knows: the panel, out of the ref a keystroke is addressed through.
     *
     * Sync State's `scratchpadId` is not a second answer to that question and is never
     * read as one. It records which Note this device has *agreed* with a Jot Server
     * about, and the two can differ in exactly one way: the panel committed a move — a
     * Note read back at the new id, proven — and this module has not recorded it yet,
     * because the Sync State write failed or the process died between the two. A move
     * that could *not* be committed is undone before the panel answers at all
     * (`AdoptOutcome`, `PullOutcome`), so there is no second way in.
     *
     * That is an unfinished commit, and neither half of it is a fact to proceed from.
     * Deciding the pass from the recorded id leaves the user typing at an id no pass ever
     * reads, with the guard refusing every pull as `unopened` and nothing able to talk the
     * two round — the state this line used to produce on any paired device. Deciding it
     * from the panel's id and leaving the record behind is no better: the device would
     * report agreement while Sync State named a Note the panel is not on, and the next
     * launch would open that one and strand the session.
     *
     * So the pass finishes it, and every place that could finish it does: `scratchpadId`
     * is written together with the agreement at all four sites that record one
     * (`recordAgreement`, `pair`, `applyPull`, and the converged read below), so a pass
     * either establishes an agreement about the body at *this* id and names it in the same
     * write, or records nothing at all. There is no third outcome in which one of the two
     * ids is treated as the winner.
     */
    const scratchpadId = deps.openScratchpadId()
    if (scratchpadId === null) return { kind: 'unavailable', what: 'scratchpad' }

    const local = await readScratchpad(scratchpadId)
    // Out of reach, which is not "empty" and not "gone": nothing may be pushed on the
    // strength of it and nothing may be written over it, so the pass ends here rather
    // than asking a server a question it could not act on the answer to.
    if (local.kind === 'unreadable') return { kind: 'unavailable', what: 'scratchpad' }

    /*
     * The three ways a remembered pull stops being evidence about the body in storage,
     * all of which have to be asked before anything acts on it (`unprovenPull`).
     *
     * The agreement names those bytes: whatever happened, the pull is settled and the
     * memory now says only that a body this device holds once came from a server, which
     * is true of most of them.
     *
     * Or the Scratchpad is no longer the id they were queued at, so nothing this pass
     * reads is about them. Measured against the panel's id, which is the only thing this
     * comparison could have meant: asked of the recorded id instead, a paired device whose
     * panel had moved dropped the memory on the very next pass, and the passes in between
     * are what it exists for.
     *
     * Or storage holds a body that is neither them nor the one they are queued over: some
     * other write won, which is to say the user typed, and if they ever type the pulled
     * body back it will be theirs.
     *
     * What is deliberately *not* here is "storage does not hold the pulled bytes". It does
     * not while the write is still queued, which is the state the memory exists for, so
     * asking that spent the memory on the first pass to look — a settled-settings pass at
     * launch, a tray reopen — and the pass after the retry landed pushed a body this
     * device only ever received.
     */
    if (unprovenPull !== null) {
      // The absence kept as an absence, so a Scratchpad that was pulled into nothing is
      // recognised by the same comparison rather than by an `''` neither side holds.
      const held = local.kind === 'body' ? local.body : null
      if (
        state.lastAgreed?.body === unprovenPull.body ||
        scratchpadId !== unprovenPull.id ||
        (held !== unprovenPull.body && held !== unprovenPull.queuedOver)
      ) {
        unprovenPull = null
      }
    }

    const client = createScratchpadClient({
      endpoint,
      token,
      // Trimmed, because a label of spaces is a field the user did not fill in.
      deviceLabel: deviceLabel.trim() === '' ? deps.info.deviceLabel : deviceLabel.trim(),
    })

    // With an agreement on record the revision to push against is known, and a `409`
    // carries the rest. What is left to discover is the other direction.
    if (state.lastAgreed !== null) {
      // "Have I changed since then", answered exactly, against the text itself: a
      // record that holds a revision and no body is not an agreement and never
      // arrives here (`asLastAgreed`), so there is no probabilistic answer to fall
      // back on and no state in which this comparison is a guess.
      //
      // *I* changed, which is not the same as "the body changed". A surviving
      // `unprovenPull` says these exact bytes are the ones a pull put on their way to
      // storage, so the local side has not moved and there is nothing here to push —
      // the `GET` below is what completes that pull, either by recording the agreement
      // the server still supports or by pulling again if it has moved on.
      if (
        local.kind === 'body' &&
        local.body !== state.lastAgreed.body &&
        unprovenPull === null
      ) {
        return recordPush(
          client,
          await client.push(local.body, state.lastAgreed.rev, scratchpadId),
          { id: scratchpadId, body: local.body, agreedBody: state.lastAgreed.body },
        )
      }

      /*
       * Nothing to push, so ask. Three ways to be here and they take the same request:
       * this device holds the body it agreed on, or it holds no Note at all — and an
       * absent Scratchpad has nothing to push either, since the one thing it must not
       * be read as is `''` (`readScratchpad`) — or it holds a body a pull put there and
       * this device never proved landed (`unprovenPull`).
       *
       * This is the whole cost of pull, and it lands where it should: a pass with local
       * changes still spends one `PUT` and no `GET`, so typing costs exactly what it
       * did. The passes that reach here are the panel opening, the interval, and a save
       * that settled on the agreed body — which is to say, the ones that exist to ask.
       */
      const read = await client.read()

      if (read.kind === 'empty') {
        /*
         * An agreement on record and a Jot Server holding nothing: its database was
         * replaced. Deliberately out of scope, and reported as the same `agreed` every
         * pass reported before pull existed — the agreement is a fact about the past
         * that a wiped relay cannot unmake, so nothing here is a lie and nothing is
         * written.
         *
         * Putting the server's Scratchpad back would mean pushing against revision 0
         * while holding an agreement at a revision the server issued, which is the
         * shape `app-store.tsx`'s bootstrap warns about in the other direction:
         * inventing a fact to escape a state. It also cannot be told apart from a
         * server answering `404` because something in front of it does, and the
         * consequence of guessing wrong is a first-push-wins race the server's own
         * `PUSH` comment describes. The device is no worse off than before pull: it
         * pushes the moment the user types, and that push restores the relay.
         */
        return { kind: 'agreed', rev: state.lastAgreed.rev, pushed: false }
      }
      if (read.kind !== 'record') return read

      const record = read.record

      // Converged, and possibly at a revision this device had not heard about: another
      // device pushed the same text, or this one's own push landed with its answer
      // lost, or a pull's write landed on the save path's retry after this engine had
      // stopped expecting it (`unprovenPull`). Recording the revision is what stops the
      // next push aiming at one the server has passed, and the body recorded is bytes
      // this device holds either way — which is what the third case needed and could not
      // get from a push.
      //
      // The identity travels with it, and this is the one site where naming it costs no
      // write of the body: the two sides hold the same body at this id, which is the proof
      // an unfinished commit was waiting for. Without it, a device that had committed a
      // move and failed to record it would sit here reporting agreement for ever while
      // Sync State named the Note it moved off — the pass that has the evidence to finish
      // the commit being the one pass that otherwise writes nothing.
      if (local.kind === 'body' && record.body === local.body) {
        const named = state.scratchpadId === scratchpadId
        if (record.rev === state.lastAgreed.rev && named) {
          return { kind: 'agreed', rev: record.rev, pushed: false }
        }
        const stored = await write({
          scratchpadId,
          lastAgreed: { rev: record.rev, body: local.body },
        })
        // Nothing recorded, and unlike `applyPull`'s last exit there is nothing to
        // remember either — which is an argument rather than an absence. Reaching here
        // means the push above was skipped, and it is skipped for exactly two reasons:
        // the local body *is* the Last-Agreed Body, so there are no received bytes in
        // play at all, or `unprovenPull` survived this pass's retention test and is
        // therefore still set when this returns. In both, the next pass asks again
        // instead of pushing.
        if (!stored) return { kind: 'unavailable', what: 'sync-state' }
        return { kind: 'agreed', rev: record.rev, pushed: false }
      }

      // Only the server has changed since the two agreed — this device holds the agreed
      // body, or holds no Note where it used to, or holds one a pull put there. So this
      // is not a Conflict, and converging means copying the server's body down. Whether
      // that may actually happen is the guard's to say, and its refusal is where a
      // Conflict can still come from: the editor is live and the body above was read
      // before this round trip (`applyPull`).
      //
      // The Last-Agreed Body travels with an absent read, and only with an absent one:
      // it is the evidence that a panel still showing the removed file's text has not
      // changed since the two agreed, which is a question a present body answers by
      // itself (`comparedWith`).
      return applyPull(record, scratchpadId, comparedWith(local, state.lastAgreed.body))
    }

    /* ------------------------------------------------------------- Pairing */

    // One `GET`, which is all Pairing costs: identity and content arrive
    // together (ADR-0003).
    const read = await client.read()

    if (read.kind === 'empty') {
      // Two empty sides are already in agreement, and pushing anyway would cost
      // more than the round trip it saves: it would create a record whose only
      // content is this device's id, so the next device to pair — the one that
      // actually has the user's text — would meet a Conflict instead of an empty
      // server it could simply push to.
      if (local.kind === 'body' && local.body === '') {
        return { kind: 'agreed', rev: 0, pushed: false }
      }

      // No local Note and nothing on the server to restore it from. Nothing is
      // established, and in particular nothing may be pushed: `''` is not what this
      // device holds, it is the answer it does not have. The save path is an upsert, so
      // the first keystroke creates the Note and the next pass is an ordinary first
      // push — which is why waiting costs nothing here.
      if (local.kind === 'absent') return { kind: 'unavailable', what: 'scratchpad' }

      // Nothing has ever been pushed, so no device has to yield and this
      // device's Scratchpad id wins — an ordinary push against revision 0.
      return recordPush(client, await client.push(local.body, 0, scratchpadId), {
        id: scratchpadId,
        body: local.body,
        // A server with no record holds no text a push could discard, which is the
        // same fact Pairing reads off an empty `record.body` below. So a `409` here
        // carrying `""` is an ordinary push, and one carrying another device's text
        // is the Conflict it is — two devices with content and no agreement between
        // them, which is what the Pairing tail says of the same pair of bodies.
        agreedBody: '',
      })
    }

    if (read.kind !== 'record') return read

    const record = read.record
    // An id of some other shape can never become this device's Scratchpad, so
    // there is no Pairing to be had and no push worth making against it.
    if (!isNoteId(record.id)) return { kind: 'malformed' }

    /*
     * Which of the three answers this is, from the two bodies alone.
     *
     * A Conflict is the state where the body changed on *both* sides since the two
     * last agreed (CONTEXT.md). Pairing has no recorded agreement to measure that
     * against, so it has to read ancestry off the bodies themselves, and there it can
     * tell apart exactly three cases. All three below are the same rule — is there a
     * body these two demonstrably have in common? — and not three special cases:
     *
     * - The bodies are equal. The two have that body in common right now, so nothing
     *   has diverged. Adopt the id, record the agreement, send nothing. Tested first,
     *   because it is the branch that can be *both* empty, and calling that "this
     *   device holds nothing" would leave it waiting for a body the server has not got
     *   either.
     *
     * - The Jot Server's body is empty. Then every local body is trivially a
     *   descendant of it, so only this side can have changed — and an empty body has
     *   no text a push could discard. Same case as the first, therefore, and the same
     *   answer plus the push it implies. It is also the routine one: a user who typed
     *   with sync off and then switched it on meets exactly this, as does anyone whose
     *   pad was cleared from another device. Calling it a Conflict wedged the device on
     *   its first request, permanently, because nothing in this slice clears a Pending
     *   Conflict (#7) — and the local text then never reached the server at all.
     *
     * - This device holds nothing where the server holds text — an empty body, or no
     *   Note at all. The mirror image, and it gets the mirror answer for the same
     *   reason: only the server has changed, so this is not a Conflict either.
     *   Converging means copying the server's body down, and that is now something this
     *   slice can do (issue #6) — under the guard, and in the same step as the Pairing
     *   it implies, because the panel is what takes both. It used to be reported as
     *   Waiting, and a device that met it stayed there for good: this is the case whose
     *   inability to converge is why Waiting exists at all.
     *
     *   Note what the mirror still does *not* license, since an empty body reads the
     *   same in both directions: an agreement recorded over `""` here would say the two
     *   sides share an empty body while the server holds text, and the next push would
     *   be accepted against that revision and replace it. So the agreement is recorded
     *   by `applyPull`, from the Note it read back after the write, and never from the
     *   comparison that authorised it.
     *
     * What is left is both sides holding different text, which is the Conflict.
     */
    if (local.kind === 'body' && (local.body === record.body || record.body === '')) {
      return pair(client, record, scratchpadId, local.body)
    }

    if (local.kind === 'absent' || local.body === '' || unprovenPull !== null) {
      // Restore, which is the same operation as the pull above and deliberately the
      // same code: whether this device's Scratchpad is empty or its file is gone,
      // nothing local is being written over, so the only question left is the guard's —
      // has the editor got text that storage has not? Wiping `userData` and re-syncing
      // arrives here, and it is what ADR-0002 means by absence never being an intent to
      // delete.
      //
      // A surviving `unprovenPull` is the same rule as the three above rather than a
      // fourth: the body in storage is one a Jot Server served at a revision this device
      // read, so it is a body the two demonstrably had in common, and it is not text the
      // user typed. Left to the Conflict below it would be a Pending Conflict raised
      // over bytes this device only ever received.
      //
      // `null` for the agreement, because there is none — Pairing is what establishes
      // one. So an editor holding text nothing else does is `unpersisted` here rather
      // than a restore, which is the honest answer while a Resolution has no stored
      // local body to offer (#7).
      return applyPull(record, scratchpadId, comparedWith(local, null))
    }

    // Both sides hold content and it differs: an ordinary Conflict, resolved by
    // the user and never by this code (ADR-0002). Recorded in full from the `GET`, so
    // the Resolution pane needs no second round trip, and pushing stops until it is
    // cleared — which issue #7 builds and this slice deliberately does not.
    //
    // The definition is established before the write, as it is at the other two places
    // one is written (`recordPush`, `applyPull`): the branches above have ruled out
    // equal bodies and an empty side, so what is left is the state CONTEXT.md names.
    // Those three sites are the only three, and each proves it holds rather than
    // inferring it from a status code, a revision comparison, or a guard that merely
    // said no.
    //
    // The id is not adopted here: Resolution needs both bodies reachable, and
    // which id wins is part of what resolving decides.
    //
    // A Pairing pass that fails at storage — the panel with nothing open, a write
    // that did not land, a Sync State that could not be written — records nothing and
    // is retried, but it can still arrive here on the retry if the user typed in
    // between: the pass that would have paired on equal bodies then sees a local body
    // that differs from the server's, with no agreement on record to prove they ever
    // matched. There is no way to tell that from a genuine Conflict without the
    // agreement, and inventing one is what the case above shows is dangerous. It stays
    // a Conflict until #7 can clear one.
    //
    // Which is also the path a Sync State record holding a revision and no body takes:
    // that is no agreement (`asLastAgreed`), so the device pairs, and lands here if its
    // text has moved on since. Recorded in full, so #7 has both bodies to offer — where
    // treating the revision as an agreement it could not measure against left the
    // device reporting a `409` it never recorded, on every pass, with nothing for a
    // Resolution to work from. And it is why such a record keeps its `scratchpadId`
    // rather than being read as unpaired: one Conflict is resolved once, while an
    // unpaired device is back on recency for good.
    //
    // The panel's id is written here, which is what keeps the Note holding the text
    // reachable across the launches a Pending Conflict now spans (`recordConflict`).
    return recordConflict(record, scratchpadId)
  }

  /**
   * Record the agreement a Resolution established, and clear the Conflict in the
   * same write.
   *
   * One write and not two, which is the only ordering that cannot strand the device.
   * Clearing first and failing to record the agreement would restart sync against a
   * Last-Agreed Body the Resolution has just made false — the next pass would push or
   * pull over the body the user kept. Recording first and failing to clear would leave
   * `runOnce` returning at its conflict gate for ever over a Conflict that no longer
   * exists, with no second button to press.
   */
  async function recordResolution(id: NoteId, rev: number, body: string): Promise<SyncStatus> {
    const stored = await write({
      scratchpadId: id,
      lastAgreed: { rev, body },
      pendingConflict: null,
    })
    if (!stored) return { kind: 'unavailable', what: 'sync-state' }
    return { kind: 'agreed', rev, pushed: true }
  }

  async function resolveOnce(choice: Resolution): Promise<SyncStatus> {
    let syncSettings: SyncSettings
    try {
      syncSettings = (await deps.settings.get()).sync
    } catch (error) {
      console.error('jot: could not read the sync settings', error)
      return { kind: 'off' }
    }

    const { enabled, endpoint, token, deviceLabel } = syncSettings
    if (!enabled || token === '') return { kind: 'off' }

    const resolved = scratchpadEndpoint(endpoint)
    if (!resolved.ok) {
      return resolved.problem === 'missing'
        ? { kind: 'off' }
        : { kind: 'refused', problem: resolved.problem }
    }

    let state: SyncState
    try {
      state = await deps.syncState.get()
    } catch (error) {
      console.error('jot: could not read the sync state', error)
      return { kind: 'unavailable', what: 'sync-state' }
    }

    // Nothing to resolve. Not an error and not a no-op either: the Conflict was
    // cleared by something else — a Resolution from a second click, or a queued pass —
    // so the honest answer is to look again rather than to report on a record that is
    // gone.
    const conflict = state.pendingConflict
    if (conflict === null) return runOnce()

    const scratchpadId = deps.openScratchpadId()
    if (scratchpadId === null) return { kind: 'unavailable', what: 'scratchpad' }

    const client = createScratchpadClient({
      endpoint,
      token,
      deviceLabel: deviceLabel.trim() === '' ? deps.info.deviceLabel : deviceLabel.trim(),
    })

    /*
     * The Jot Server as it is *now*, which is the correction this whole method exists
     * for. `conflict.rev` was the revision when the Conflict was recorded, and a
     * Pending Conflict is explicitly allowed to sit indefinitely (ADR-0002) — long
     * enough for another device to push again. Pushing against the stale number is a
     * `409`, which `recordPush` turns into a fresh Pending Conflict, so the user's
     * click would leave the pane exactly where it was.
     */
    const current = await client.read()

    if (current.kind === 'unauthorized') return { kind: 'unauthorized' }
    if (current.kind === 'unreachable') return { kind: 'unreachable' }
    if (current.kind === 'refused') return { kind: 'refused', problem: current.problem }
    if (current.kind === 'rejected') return { kind: 'rejected', httpStatus: current.httpStatus }
    if (current.kind === 'malformed') return { kind: 'malformed' }
    if (current.kind === 'oversized') return { kind: 'oversized' }

    // A server holding nothing is a Resolution with only one body left in it, whichever
    // the user picked: there is no server body to keep, and `rev: 0` is what an
    // ordinary first push is aimed at. Falling through to "keep local" is therefore not
    // a special case but the same one — and a "keep server" click that lands here is
    // the user asking to keep a body the server has since dropped, which is text this
    // device would otherwise be the last copy of.
    const serverRecord = current.kind === 'record' ? current.record : null

    if (choice === 'server') {
      if (serverRecord === null) return { kind: 'unavailable', what: 'scratchpad' }

      /*
       * The body the user was shown, or a different one. A Conflict can sit for days,
       * and "keep server" is a choice about text that was on screen — so a server that
       * has moved since means the choice was made about a body that no longer exists.
       * Re-recording the Conflict is the only honest answer: nothing is discarded, and
       * the pane reopens showing what the server holds now.
       */
      if (serverRecord.body !== conflict.body) {
        return recordConflict(serverRecord, scratchpadId)
      }

      const applied = await deps.applyResolvedBody({
        id: scratchpadId,
        body: serverRecord.body,
      })
      // The write did not land, or did not read back holding what the panel put there.
      // The Conflict stays recorded, deliberately: an agreement written now would name
      // a body the Scratchpad does not hold, and the local text the user chose to
      // discard is still the only thing on this device.
      if (applied.kind === 'unwritten') return { kind: 'unavailable', what: 'scratchpad' }

      // The Note as it was read back, not the body that was sent. A keystroke that beat
      // the write is the body this device now holds, and recording anything else would
      // put a Last-Agreed Body on file that no side has.
      return recordResolution(scratchpadId, serverRecord.rev, applied.note.body)
    }

    const local = await readScratchpad(scratchpadId)
    // Unreadable is not empty: pushing `''` here would replace the Scratchpad on every
    // paired device with nothing, which is the opposite of what "keep mine" asked for.
    if (local.kind === 'unreadable') return { kind: 'unavailable', what: 'scratchpad' }
    // Absent, with a Conflict on record. There is no local body to keep, so there is
    // nothing this click can push; the Conflict stays, and `runOnce`'s restore path is
    // what has something to say about a Scratchpad storage no longer holds.
    if (local.kind === 'absent') return { kind: 'unavailable', what: 'scratchpad' }

    /*
     * The persisted body rather than the editor's live text, which is this module's
     * rule everywhere (`readScratchpad`) and costs nothing here: anything typed since
     * the last save is one debounce away from disk, and the pass that save triggers
     * pushes it as an ordinary push against the agreement this Resolution is about to
     * record. So the newer text reaches the server a moment later, and never as a
     * Conflict.
     */
    const rev = serverRecord === null ? 0 : serverRecord.rev
    const outcome = await client.push(local.body, rev, scratchpadId)

    if (outcome.kind === 'accepted') {
      return recordResolution(scratchpadId, outcome.rev, local.body)
    }

    /*
     * The server moved between the read above and this push — another device, in the
     * seconds the Resolution took. Not this click's Conflict any more, so it is
     * recorded as the new one it is and the pane reopens on it. Deliberately not
     * retried: a second aim would be racing the same device again, and a Resolution
     * that silently re-pushed over whatever arrived in between would be discarding a
     * body the user was never shown.
     */
    if (outcome.kind === 'conflict') return recordConflict(outcome.record, scratchpadId)

    if (outcome.kind === 'unauthorized') return { kind: 'unauthorized' }
    if (outcome.kind === 'unreachable') return { kind: 'unreachable' }
    if (outcome.kind === 'refused') return { kind: 'refused', problem: outcome.problem }
    if (outcome.kind === 'too-large') {
      return { kind: 'too-large', bytes: outcome.bytes, limit: outcome.limit }
    }
    if (outcome.kind === 'malformed') return { kind: 'malformed' }
    if (outcome.kind === 'oversized') return { kind: 'oversized' }
    return { kind: 'rejected', httpStatus: outcome.httpStatus }
  }

  function sync(): Promise<SyncStatus> {
    if (inFlight === null) {
      const run = runOnce()
        .catch((error: unknown): SyncStatus => {
          // Nothing above is meant to throw — every outcome is a value — so this
          // is a bug rather than a failure mode. It still must not reject: the
          // caller is a React effect with nowhere to put a rejection, and a
          // rejected promise here would leave `inFlight` cleared but the UI
          // reporting whatever it saw last.
          console.error('jot: sync failed unexpectedly', error)
          return { kind: 'unavailable', what: 'sync-state' }
        })
        .finally(() => {
          // Only if it is still this pass's. A Resolution puts its own run in
          // `inFlight` so that `sync` queues behind it (`resolve`), and an
          // unconditional clear here would drop that one on the floor — leaving a
          // pass free to run against the Sync State a Resolution is mid-write on.
          if (inFlight === run) inFlight = null
        })
      inFlight = run
      return run
    }

    // `finally` above has already cleared `inFlight` by the time this
    // continuation runs, so the recursive call starts a genuine second pass.
    queued ??= inFlight.then(() => {
      queued = null
      return sync()
    })
    return queued
  }

  function resolve(choice: Resolution): Promise<SyncStatus> {
    const begin = (): Promise<SyncStatus> => {
      const run = resolveOnce(choice)
        .catch((error: unknown): SyncStatus => {
          // Same contract as `sync`: every outcome above is a value, so arriving here
          // is a bug — and it still must not reject, because the caller is a button
          // handler with nowhere to put one.
          console.error('jot: resolving the conflict failed unexpectedly', error)
          return { kind: 'unavailable', what: 'sync-state' }
        })
        .finally(() => {
          if (inFlight === run) inFlight = null
        })
      inFlight = run
      return run
    }

    // Behind whatever is running, and in front of whatever `sync` queues next: a pass
    // that read the Pending Conflict before this write and recorded after it would put
    // back the record the Resolution just cleared. `begin` is passed as both handlers
    // because a failed pass is still a finished one, and a Resolution the user asked
    // for must not be lost to it.
    return inFlight === null ? begin() : inFlight.then(begin, begin)
  }

  return { sync, resolve }
}
